import {
  ApiError,
  FormRecipient,
  FormSendResult,
  FormsService,
} from "client/openapi";
import Notifications from "util/notifications";

const BATCH = 20;

/** The readable reason in an API error, or the fallback. */
export function errorText(e: unknown, fallback: string): string {
  const body = (e as ApiError)?.body;
  const reason = body?.detail ?? body?.error;
  if (typeof reason === "string") return reason;
  return reason?.[0]?.msg ?? fallback; // FastAPI's 422 lists what was wrong
}

/**
 * Send a form to people a few at a time, so a long list never holds one
 * request open for long. Each person gets their own link.
 */
export async function sendFormInBatches({
  orgId,
  formId,
  recipients,
  sms,
  email,
  content,
  subject,
}: {
  orgId: number;
  formId: number;
  recipients: FormRecipient[];
  sms: boolean;
  email: boolean;
  content?: string;
  subject?: string;
}): Promise<FormSendResult[]> {
  const results: FormSendResult[] = [];
  for (let i = 0; i < recipients.length; i += BATCH) {
    try {
      const response = await FormsService.sendFormToPeople({
        orgId,
        requestBody: {
          form_id: formId,
          recipients: recipients.slice(i, i + BATCH),
          sms,
          email,
          content: content || undefined,
          subject: subject || undefined,
        },
      });
      results.push(...response.results);
    } catch (e) {
      if (results.length === 0) throw e; // nothing went out yet
      // Earlier batches went out: report them, and everyone after as not sent.
      const reason =
        "The send stopped early. Check this person's forms before sending again.";
      results.push(
        ...recipients.slice(i).map((r) => ({
          ...r,
          name: "",
          status: FormSendResult.status.FAILED,
          reason,
        }))
      );
      break;
    }
  }
  return results;
}

/** One plain sentence about how a send went. */
export function sendSummary(results: FormSendResult[]): string {
  const sent = results.filter((r) => r.status === "sent");
  const notSent = results.filter((r) => r.status !== "sent");
  if (notSent.length === 0) {
    return sent.length === 1
      ? `Form sent to ${sent[0].name}.`
      : `Form sent to ${sent.length} people.`;
  }
  const reasons = notSent
    .slice(0, 3)
    .map((r) => `${r.name || "Someone"}: ${r.reason}`)
    .join(" ");
  const more = notSent.length > 3 ? ` And ${notSent.length - 3} more.` : "";
  return `Sent to ${sent.length}, not sent to ${notSent.length}. ${reasons}${more}`;
}

/** After someone is added in an invite dialog: send them the chosen form, if any. */
export async function sendChosenForm({
  orgId,
  formId,
  sms,
  email,
  personType,
  personId,
}: {
  orgId: number;
  formId: number | null;
  sms: boolean;
  email: boolean;
  personType: FormRecipient["person_type"];
  personId?: number | null;
}): Promise<boolean> {
  if (formId === null || !personId) return false;
  if (!sms && !email) {
    Notifications.error("The form was not sent: pick SMS, Email or both.");
    return false;
  }
  try {
    const results = await sendFormInBatches({
      orgId,
      formId,
      sms,
      email,
      recipients: [{ person_type: personType, person_id: personId }],
    });
    const summary = sendSummary(results);
    const allSent = results.every((r) => r.status === "sent");
    if (allSent) Notifications.success(summary);
    else Notifications.error(summary);
    return allSent;
  } catch (e) {
    Notifications.error(errorText(e, "The form could not be sent."));
    return false;
  }
}
